/**
 * Theme configuration
 */

import { createTheme } from '@mui/material/styles';
import { PaletteOptions, TypographyVariantsOptions, Components, Theme } from '@mui/material';

const theme = createTheme();

// 1. Define the palette options
const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: {
    main: '#6EC8E2',
    dark: '#2E9BB9',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#000000',
    contrastText: '#FFFFFF',
  },
  info: {
    main: '#0174FC',
    light: '#6EC8E2',
    contrastText: '#ffffff',
  },
  background: {
    default: '#FAF9F8',
    paper: '#FFFFFF',
  },
  divider: '#E2F4F9',
  text: {
    secondary: '#bbbbbb',
  },
  grey: {
    '50': '#dddddd',
  },
  error: {
    main: '#D95153',
  },
  success: {
    main: '#51D9AA',
  },
};
const darkPalette: PaletteOptions = {
  mode: 'dark',
  primary: {
    main: '#90caf9',
  },
  secondary: {
    main: '#ce93d8',
  },
  background: {
    default: '#121212',
    paper: '#1d1d1d',
  },
};

// 2. Define the typography options
const typography: TypographyVariantsOptions = {
  fontFamily: '"Noto Sans JP", sans-serif',
  h1: {
    fontSize: '2rem',
    fontWeight: 600,
  },
  h2: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.25rem',
    },
  },
  body1: {
    fontSize: '16px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '14px',
    },
  },
  h3: {
    fontSize: '20px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
    },
  },
  h4: {
    fontSize: '18px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '16px',
    },
  },
  h5: {
    fontSize: '12px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
    },
  },
};

// 3. Define the component overrides
const components: Components<Theme> = {
  MuiContainer: {
    styleOverrides: {
      maxWidthSm: {
        maxWidth: '720px',
        '@media (min-width:600px)': {
          maxWidth: '720px',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 30,
        textTransform: 'none',
      },
      outlined: {
        border: '2px solid #DDDDDD',
        color: 'black',
      },
      outlinedSecondary: {
        borderColor: '#DDDDDD',
      },
    },
  },
  MuiPagination: {
    styleOverrides: {
      root: {
        '& .MuiPagination-ul': {
          justifyContent: 'end',
        },
      },
      outlined: {
        '& .MuiPaginationItem-outlined': {
          borderColor: '#000000',
          color: '#000000',
          borderRadius: '5px',
        },
        '& .Mui-selected': {
          backgroundColor: '#6EC8E2 !important',
          color: '#ffffff !important',
          border: '1px solid #000000 !important',
        },
        '& .MuiPaginationItem-outlined:hover': {
          backgroundColor: '#6EC8E298',
          color: '#ffffff',
        },
      },
    },
  },
  MuiDialog: {
    styleOverrides: {
      root: {
        '& .MuiDialog-paper': {
          marginTop: '10px',
          borderRadius: '10px',
          boxShadow: 'none',
          padding: '15px 15px 25px 15px',
        },
        '& .MuiDialog-paperWidthMd': {
          width: '480px',
        },
        '& .MuiDialog-paperWidthXl': {
          width: '625px',
        },
      },
    },
  },
  MuiDialogTitle: {
    styleOverrides: {
      root: {
        padding: 0,
        fontWeight: 700,
        fontSize: '16px',
      },
    },
  },
  MuiDialogContent: {
    styleOverrides: {
      root: {
        padding: 0,
        overflow: 'unset',
        width: '100%',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        padding: '16px 0 0 0',
        '& > :not(style) ~ :not(style)': {
          marginLeft: '18px',
        },
      },
    },
  },
  MuiSvgIcon: {
    styleOverrides: {
      fontSizeSmall: {
        fontSize: '0.6rem',
      },
    },
  },
  MuiTableCell: {
    styleOverrides: {
      root: {
        padding: '8px 18px',
        border: 'none',
        whiteSpace: 'nowrap',
      },
      head: {
        height: '58px',
        fontSize: '16px',
        backgroundColor: '#F8F8F8',
        color: '#000000',
        userSelect: 'none',
      },
      body: {
        fontSize: '14px',
        fontWeight: '500',
        whiteSpace: 'pre',
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        background: '#FFFFFF',
        borderRadius: '10px',
        '& fieldset': {
          border: '1px solid #DDDDDD',
          transition: 'all 0.2s ease',
        },
        '&:hover fieldset': {
          borderColor: '#6EC8E2 !important',
        },
        '&.Mui-disabled:hover fieldset': {
          borderColor: '#DDDDDD !important',
        },
        '&.Mui-disabled fieldset': {
          borderColor: '#DDDDDD !important',
        },
        '&.Mui-focused fieldset': {
          border: '1px solid #6EC8E2 !important',
        },
        '&.MuiInputBase-root': {
          paddingRight: '10px ',
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        padding: 0,
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: {
        '&.Mui-checked': {
          color: '#666666',
        },
      },
    },
  },
};

// 4. Create the theme
export const lightTheme = createTheme({
  palette: lightPalette,
  typography,
  components,
});

export const darkTheme = createTheme({
  palette: darkPalette,
  typography,
  components,
});
